/**
 * Demo mode only: a plausible transcript for a mock session, so the drawer timeline can be
 * seen without a hub. Built deterministically from the session's own fields (title, subagents,
 * workflows, last text, counts). Never used against a live hub.
 */
import type { Session } from '@orgus/protocol';
import { agentKindOf, sessionAskOf } from '@orgus/protocol';
import { speakerOf } from '../adapters/state.ts';
import { parseTranscript } from './transcript.ts';
import type { TranscriptEntry } from './transcript.ts';

const TOOLS: Array<[string, string]> = [
  ['Read', 'store.ts'],
  ['Grep', 'requireScope\\('],
  ['Bash', 'Run the unit tests'],
  ['Edit', 'Sessions.tsx'],
  ['Read', 'SPEC.md'],
  ['Glob', 'src/**/*.test.ts'],
  ['Bash', 'Typecheck the workspace'],
  ['Edit', 'transcript.ts'],
  ['WebSearch', 'radix dialog focus trap side sheet'],
  ['Write', 'labels.ts'],
  ['Bash', 'git status --short'],
  ['Read', 'mock.ts'],
];

const SAYS = [
  'Reading how the current code handles this before changing anything.',
  'Found the cause. Making the change in one place so every caller gets it.',
  'Tests pass locally. Checking the edge cases next.',
  'That works. Tidying up and re-running the checks.',
];

/** A reply as an agent writes it, in Markdown: the drawer renders it (lists, bold, code, a link) and folds it. */
const RICH = [
  'Found it. The drawer width and the table columns disagree at the `md` breakpoint.',
  '',
  '**What changes**',
  '',
  '- **Desktop column:** the drawer keeps `w-drawer` and the table gives up its last column.',
  '- **Phone:** the drawer takes the full width; the list stays underneath.',
  '- **Tests:** `layout.test.ts` covers both widths.',
  '',
  '```ts',
  "export function drawerWidth(viewport: number): 'full' | 'drawer' {",
  "  return viewport < 768 ? 'full' : 'drawer';",
  '}',
  '```',
  '',
  'Next:',
  '',
  '1. Make the change in `layout.ts`.',
  '2. Run `pnpm test:affected`.',
  '3. Take screenshots at 1440 and 390 wide.',
  '',
  'The widths follow the [Tailwind max-width scale](https://tailwindcss.com/docs/max-width).',
].join('\n');

function seeded(id: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

/** A clock time for the demo log: `hh:mm:ss` of a timestamp. */
const clock = (at: number) => new Date(at).toTimeString().slice(0, 8);
/** A message in the session text format: further lines start with two spaces (timeline-format.ts). */
const lines = (text: string) => text.split('\n').join('\n  ');

/**
 * What an ACP agent's feed carries, in the agent's own text format, read by the real parser: a
 * line from Orgus, the prompt, reasoning steps (each a `~ ` line, its bold subject first), tools,
 * and a reply that is one run of `**Subject**` pieces, glued the way older feeds stored it.
 */
function acpDemo(s: Session, speaker: string): TranscriptEntry[] {
  const t = (i: number) => clock(s.startedAt + i * 7_000);
  const log = [
    `[${t(0)}] Starting ${speaker} (ACP) in \`${s.cwd}\``,
    `[${t(0)}] Set mode to **autoEdit**`,
    `[${t(0)}] You: ${lines(s.title)}`,
    `[${t(1)}] ~ ${lines('**Analyzing Telemetry Data**\n\nThe landing failed in `pnpm test:affected`. The usage numbers come from telemetry, so I will move on to the otel-file next.')}`,
    `[${t(2)}] ~ ${lines("**Investigating OtelFile Implementation**\n\nI'm now diving into how `otel-file.ts` reads spans: each `api_response` adds input, output and cached tokens.")}`,
    `[${t(3)}] > Read: otel-file.ts`,
    `[${t(3)}] > Grep: cachedTokens`,
    // An older feed: the step glued to the sentence before it; the parser gives it its break back.
    `[${t(4)}] ~ ${lines('The cached count is added on top of input.**Examining Token Counting Logic** Input already includes the cached tokens, so they are counted twice.')}`,
    `[${t(5)}] > Edit: otel-file.ts`,
    `[${t(6)}] > Bash: pnpm vitest run otel-file.test.ts`,
    `[${t(7)}] ${speaker}: ${lines(
      [
        'The landing failed because cached tokens were counted twice.**Cause**',
        '',
        '`otel-file.ts` added `cached` to `input`, but Gemini reports input with the cached tokens already in it.',
        '',
        '**Fix**',
        '',
        '- Input is now `input - cached`, never below zero.',
        '- A test covers a response with only cached tokens.',
        '',
        '```ts',
        'const input = Math.max(0, num(o.input) - cached);',
        '```',
        '',
        '**Checks**',
        '',
        '1. `otel-file.test.ts` passes.',
        '2. Landed again with `orgus land`.',
      ].join('\n'),
    )}`,
    `[${t(8)}] Turn ended: end_turn`,
    '',
  ].join('\n');
  return parseTranscript(log, 400, s.startedAt + 60_000, speaker);
}

export function demoTranscript(s: Session): TranscriptEntry[] {
  if (agentKindOf(s) === 'gemini-cli') return acpDemo(s, speakerOf(s));
  const rand = seeded(s.id);
  const start = s.startedAt;
  const end = Math.max(start + 1000, s.lastActivity);
  const span = end - start;
  const out: TranscriptEntry[] = [];
  let k = 0;
  const add = (e: Omit<TranscriptEntry, 'key'>) => out.push({ key: `d${k++}`, ...e });

  // An ACP agent (Gemini CLI, OpenCode, ...) shares its reasoning, and Orgus notes what it set.
  const acp = agentKindOf(s) !== 'claude-code' && agentKindOf(s) !== 'codex';
  if (acp) add({ kind: 'system', at: start, text: 'Set mode to autoEdit' });
  add({ kind: 'prompt', at: start, text: s.title });
  if (acp) add({ kind: 'thinking', at: start + 200, text: 'The failing check names the lander. Its log should say which step stopped, so I read that before touching any code.' });
  const steps = Math.min(16, Math.max(3, Math.round(s.toolCalls / 9)));
  for (let i = 0; i < steps; i++) {
    const at = start + Math.round(((i + 1) / (steps + 2)) * span);
    if (i % 4 === 0) add({ kind: 'say', at, text: SAYS[(i / 4) % SAYS.length]! });
    const [tool, text] = TOOLS[Math.floor(rand() * TOOLS.length)]!;
    add({ kind: 'tool', at: at + 400, tool, text });
    if (tool === 'Bash' && rand() < 0.12) add({ kind: 'error', at: at + 900, text: 'Exit code 1. One test failed: expected 3 rows, got 2.' });
  }
  add({ kind: 'say', at: start + Math.round(span * 0.5), text: RICH });
  if (s.turns > 3) add({ kind: 'prompt', at: start + Math.round(span * 0.55), text: 'Keep going, and run the checks before you finish.' });
  for (const a of s.subagents) add({ kind: 'tool', at: a.startedAt, tool: 'Agent', text: a.description });
  for (const w of s.workflows) add({ kind: 'tool', at: w.startedAt, tool: 'Workflow', text: w.name });
  // A waiting session's whole last message (ADR 0056), else its one-line last text.
  const last = sessionAskOf(s.ext)?.text || s.lastText;
  if (last) add({ kind: 'say', at: end, text: last });
  return out.sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
}
